import { useState } from 'react'
import { GOAL_CARD_TEXT } from '@anqi/shared'
import type { Action, ChatPayload, PlayerView, Pos } from '../protocol-types'
import { Board, seatStyle } from '../components/Board'
import { GoalCard } from '../components/GoalCard'
import { ActionBar, type Mode } from '../components/ActionBar'
import { ChatBar } from '../components/ChatBar'
import { RulesSheet } from '../components/RulesSheet'

/**
 * 对局屏。竖屏单列，从上到下：
 *   自己的暗牌（可折叠）→ 棋盘 → 行动区 → 对手状态 → 聊天条
 *
 * 顺序是按「盯着看的频率」排的：棋盘最常看，所以放在拇指容易到的中上部；
 * 暗牌看一眼就够，可以折起来给棋盘让位置。
 */
interface GameScreenProps {
  view: PlayerView
  messages: ChatPayload[]
  onAct(action: Action): void
  onChat(text: string, preset: boolean): void
  lastError: string | null
}

export function GameScreen({ view, messages, onAct, onChat, lastError }: GameScreenProps) {
  const [selected, setSelected] = useState<Pos | null>(null)
  const [mode, setMode] = useState<Mode>('place')
  const [goalOpen, setGoalOpen] = useState(true)
  const [rulesOpen, setRulesOpen] = useState(false)

  const myTurn = view.current === view.me.seat && !view.ended

  return (
    <div className="mx-auto flex max-w-md flex-col gap-3 p-3">
      {rulesOpen && <RulesSheet onClose={() => setRulesOpen(false)} />}

      {/* 规则入口。对局中必须随时能看 —— 忘了某张卡要几颗子，
          没有语音的三个人只能自己查，查不到就只能瞎下。 */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setRulesOpen(true)}
          className="min-h-[36px] rounded-full border border-slate-600 px-3 text-xs text-slate-300"
        >
          ？游戏说明
        </button>
      </div>

      {/* 自己的暗牌 */}
      <div>
        <button
          type="button"
          onClick={() => setGoalOpen((v) => !v)}
          className="mb-1 flex min-h-[44px] w-full items-center justify-between px-1 text-sm text-slate-400"
        >
          <span>你的目标（只有你看得到）</span>
          <span>{goalOpen ? '收起 ▲' : '展开 ▼'}</span>
        </button>
        {goalOpen && (
          <GoalCard
            id={view.me.goal}
            siegeTarget={view.me.siegeTarget}
            ownerLabel="你的目标"
            revealed={view.me.goalRevealed}
          />
        )}
      </div>

      {/* 轮次与回合数 */}
      <div className="flex items-center justify-between text-sm">
        <span className={myTurn ? 'font-bold text-amber-300' : 'text-slate-400'}>
          {view.ended ? '对局结束' : myTurn ? '轮到你' : `等 ${seatStyle(view.current).label}方`}
        </span>
        <span className="text-slate-500">
          第 {view.turnCount} / {view.config.maxTurns} 回合
        </span>
      </div>

      <Board
        board={view.board}
        legalActions={view.myLegalActions}
        selected={selected}
        mode={mode}
        onSelect={(p) => setSelected(p)}
      />

      <ActionBar
        view={view}
        mode={mode}
        onModeChange={setMode}
        selected={selected}
        onClearSelection={() => setSelected(null)}
        onAct={(a) => {
          setSelected(null)
          onAct(a)
        }}
        lastError={lastError}
      />

      {/* 对手状态。只显示公开信息 —— 已亮的牌才显示牌面 */}
      <div className="grid grid-cols-2 gap-2">
        {view.opponents.map((o) => {
          const st = seatStyle(o.seat)
          return (
            <div key={o.seat} className="rounded-lg bg-slate-800 p-2 text-xs">
              <p className="flex items-center gap-1 font-semibold">
                <span className={st.text}>{st.glyph}</span>
                {o.displayName}
              </p>
              <p className="mt-1 text-slate-400">
                子 {o.stonesLeft} · 禁 {o.blocksLeft}
              </p>
              {o.revealedGoal !== null ? (
                <p className="mt-1 font-semibold text-amber-300">
                  已亮：{GOAL_CARD_TEXT[o.revealedGoal].name}
                </p>
              ) : (
                <p className="mt-1 text-slate-500">暗牌</p>
              )}
              {o.skipNextTurn && <p className="mt-1 text-red-400">下回合被跳过</p>}
            </div>
          )
        })}
      </div>

      <ChatBar messages={messages} onSend={onChat} />
    </div>
  )
}
